import HelperMixin from '../../Shared/HelperMixin.vue';
import TimeWindowsEditor from './TimeWindowsEditor/TimeWindowsEditor.vue';

// Sprint A2 (Agent-A2 / 2026-05-20) — UI for §2 dynamic pricing engine.
// Backs PR-11 (RAFIQ-COMPLETION-STATUS row): admin can read every
// PricingConfig row, edit base/km/min/wait + surge multipliers, and save
// via PUT api/admin/Pricing/Configuration/{categoryId}.
//
// The page intentionally surfaces "Active Surge" (Pricing/Surge/Active)
// next to the configured surge values so the operator can verify whether
// PeakHour/Night currently match a PricingTimeWindow row.

export default {
    name: 'Pricing',
    mixins: [HelperMixin],
    components: { TimeWindowsEditor },

    async created() {
        this.loginDetails = await this.CheckLoginStatus();

        if (!this.requireUserType([1])) return;

        await this.Load();
    },

    data() {
        return {
            Configs: [],
            ActiveSurges: [],
            SelectedCategoryId: null,
            Current: null,
            Saving: false,
        };
    },

    computed: {
        ActiveSurgeForCategory() {
            if (!this.SelectedCategoryId || !this.ActiveSurges || !this.ActiveSurges.length)
                return null;
            return this.ActiveSurges.find(a => a.categoryId === this.SelectedCategoryId) || null;
        },
        SelectedCategoryName() {
            const c = (this.Configs || []).find(x => x.categoryId === this.SelectedCategoryId);
            return c ? (c.categoryName || ('فئة #' + c.categoryId)) : '';
        }
    },

    methods: {
        async Load() {
            this.$blockUI.Start();
            try {
                const cfgRes = await this.$http.GetPricingConfiguration();
                this.Configs = cfgRes.data || [];
                if (!this.SelectedCategoryId && this.Configs.length) {
                    this.SelectedCategoryId = this.Configs[0].categoryId;
                }
                this.SelectCategory(this.SelectedCategoryId);

                try {
                    const surgeRes = await this.$http.GetActiveSurges();
                    this.ActiveSurges = surgeRes.data || [];
                } catch (e) {
                    this.ActiveSurges = [];
                }
            } catch (e) {
                // The API rejects with a BARE JSON STRING body (BackMessages.NotAuthorized),
                // so `.Message` was always undefined and the real reason — session expired,
                // not a super admin, or the server simply not running — was replaced by this
                // generic text. ApiError reads every shape and separates transport failures.
                const msg = this.$helper && this.$helper.ApiError
                    ? this.$helper.ApiError(e, 'تعذّر تحميل إعدادات التسعير')
                    : (e?.response?.data?.Message || 'تعذّر تحميل إعدادات التسعير');
                this.$helper && this.$helper.FailMessage && this.$helper.FailMessage(msg);
            } finally {
                this.$blockUI.Stop();
            }
        },

        SelectCategory(categoryId) {
            this.SelectedCategoryId = categoryId;
            const found = this.Configs.find(c => c.categoryId === categoryId);
            this.Current = found ? Object.assign({}, found) : null;
        },

        async Save() {
            if (!this.Current) return;
            this.Saving = true;
            try {
                const body = Object.assign({}, this.Current);
                const res = await this.$http.UpdatePricingConfiguration(this.SelectedCategoryId, body);
                const changes = (res && res.data && res.data.changedFields) || 0;
                if (this.$helper && this.$helper.SuccessMessage) {
                    this.$helper.SuccessMessage(`تم حفظ ${changes} تعديل بنجاح`);
                } else {
                    alert(`تم حفظ ${changes} تعديل بنجاح`);
                }
                await this.Load();
            } catch (e) {
                const msg = e?.response?.data?.Message || e?.response?.data?.Code || 'فشل حفظ التعديلات';
                if (this.$helper && this.$helper.FailMessage) {
                    this.$helper.FailMessage(msg);
                } else {
                    alert(msg);
                }
            } finally {
                this.Saving = false;
            }
        },

        OpenChangeLog() {
            this.$router.push('/PricingChangeLog');
        },

        // Emitted by the TimeWindowsEditor sub-component after a successful
        // PUT. We re-pull Surge/Active so the "Active Now" panel below
        // reflects whether the freshly saved window currently matches local
        // Tripoli time — closes the visual loop the operator expects.
        async OnTimeWindowsSaved() {
            try {
                const surgeRes = await this.$http.GetActiveSurges();
                this.ActiveSurges = surgeRes.data || [];
            } catch (e) {
                this.ActiveSurges = [];
            }
        }
    }
};
